import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiCheck,
  FiChevronDown,
  FiHeart,
  FiSliders,
  FiX,
} from "react-icons/fi";
import productData, { siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";
import "../css/shop.scss";

const getColorName = (color) =>
  typeof color === "string" ? color : color?.name || "";

const getWishlistId = (item) =>
  typeof item === "object" && item !== null ? item.id : item;

const formatProductPrice = (price) => {
  if (typeof siteConfig?.formatPrice === "function") {
    return siteConfig.formatPrice(price);
  }

  return `${siteConfig?.currencySymbol || "$"} ${Number(price).toLocaleString(
    "en-US"
  )}`;
};

export function ProductCard({ product, eager = false }) {
  const cartContext = useContext(CartContext) || {};
  const wishlistItems = cartContext.wishlistItems || [];
  const toggleWishlist = cartContext.toggleWishlist || (() => {});
  const [primaryLoaded, setPrimaryLoaded] = useState(false);
  const [primaryError, setPrimaryError] = useState(false);
  const [secondaryError, setSecondaryError] = useState(false);

  const images = Array.isArray(product.images) ? product.images : [];
  const primaryImage = product.image || images[0];
  const secondaryImage = images.find((image) => image !== primaryImage);
  const isWishlisted = wishlistItems.some(
    (item) => String(getWishlistId(item)) === String(product.id)
  );
  const colorCount = Array.isArray(product.colors) ? product.colors.length : 0;

  const handleWishlist = () => {
    toggleWishlist(product.id);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <article className="product-card">
      <div
        className={`product-card__media ${
          primaryLoaded ? "is-loaded" : "is-loading"
        }`}
      >
        <Link
          className="product-card__image-link"
          to={`/shopDetails/${product.id}`}
          onClick={scrollToTop}
          aria-label={`View ${product.name}`}
        >
          {!primaryError ? (
            <img
              className="product-card__image product-card__image--primary"
              src={primaryImage}
              alt={product.name}
              loading={eager ? "eager" : "lazy"}
              onLoad={() => setPrimaryLoaded(true)}
              onError={() => {
                setPrimaryLoaded(true);
                setPrimaryError(true);
              }}
            />
          ) : (
            <span className="product-card__image-error" aria-hidden="true">
              Image unavailable
            </span>
          )}

          {secondaryImage && !secondaryError && (
            <img
              className="product-card__image product-card__image--secondary"
              src={secondaryImage}
              alt=""
              loading="lazy"
              onError={() => setSecondaryError(true)}
            />
          )}
        </Link>

        {product.badge && (
          <span className="product-card__badge">{product.badge}</span>
        )}

        <button
          className={`product-card__wishlist ${
            isWishlisted ? "is-active" : ""
          }`}
          type="button"
          onClick={handleWishlist}
          aria-label={`${isWishlisted ? "Remove" : "Add"} ${
            product.name
          } ${isWishlisted ? "from" : "to"} wishlist`}
          aria-pressed={isWishlisted}
          title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
        >
          <FiHeart aria-hidden="true" />
        </button>
      </div>

      <div className="product-card__details">
        <Link
          className="product-card__name"
          to={`/shopDetails/${product.id}`}
          onClick={scrollToTop}
        >
          {product.name}
        </Link>
        <p className="product-card__price">{formatProductPrice(product.price)}</p>
        {colorCount > 1 && (
          <p className="product-card__colors">
            {colorCount} {colorCount === 1 ? "Colour" : "Colours"}
          </p>
        )}
      </div>
    </article>
  );
}

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cartContext = useContext(CartContext) || {};
  const wishlistItems = useMemo(
    () => cartContext.wishlistItems || [],
    [cartContext.wishlistItems]
  );
  const [sortBy, setSortBy] = useState("featured");
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const closeButtonRef = useRef(null);

  const searchTerm = (searchParams.get("search") || "").trim();
  const activeCategory = searchParams.get("category") || "";
  const activeGender = searchParams.get("gender") || "";
  const wishlistView = searchParams.get("view") === "wishlist";

  const categories = useMemo(
    () =>
      [...new Set(productData.map((product) => product.category).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    []
  );

  const sizes = useMemo(
    () =>
      [...new Set(productData.flatMap((product) => product.size || []))].sort(
        (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })
      ),
    []
  );

  const colors = useMemo(
    () =>
      [
        ...new Set(
          productData.flatMap((product) =>
            (product.colors || []).map(getColorName).filter(Boolean)
          )
        ),
      ].sort((a, b) => a.localeCompare(b)),
    []
  );

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filtersOpen]);

  const updateUrlFilter = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) nextParams.set(key, value);
    else nextParams.delete(key);

    setSearchParams(nextParams, { replace: true });
  };

  const toggleArrayFilter = (value, setter) => {
    setter((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  };

  const clearFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("category");
    nextParams.delete("gender");
    nextParams.delete("search");
    setSearchParams(nextParams, { replace: true });
    setSelectedSizes([]);
    setSelectedColors([]);
    setSortBy("featured");
  };

  const filteredProducts = useMemo(() => {
    const wishlistIds = new Set(wishlistItems.map((item) => String(getWishlistId(item))));
    const normalizedSearch = searchTerm.toLowerCase();

    const filtered = productData.filter((product) => {
      const productColors = (product.colors || []).map(getColorName);
      const searchable = [
        product.name,
        product.category,
        product.gender,
        product.material,
        product.description,
        ...(product.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      const gender = String(product.gender || "unisex").toLowerCase();
      const genderMatch =
        !activeGender ||
        gender === activeGender.toLowerCase() ||
        gender === "unisex";

      return (
        (!wishlistView || wishlistIds.has(String(product.id))) &&
        (!normalizedSearch || searchable.includes(normalizedSearch)) &&
        (!activeCategory ||
          String(product.category).toLowerCase() === activeCategory.toLowerCase()) &&
        genderMatch &&
        (!selectedSizes.length ||
          (product.size || []).some((size) => selectedSizes.includes(size))) &&
        (!selectedColors.length ||
          productColors.some((color) => selectedColors.includes(color)))
      );
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "newest") return Number(b.id) - Number(a.id);
      if (sortBy === "price-ascending") return Number(a.price) - Number(b.price);
      if (sortBy === "price-descending") return Number(b.price) - Number(a.price);
      if (sortBy === "name") return a.name.localeCompare(b.name);

      return Number(Boolean(b.featured)) - Number(Boolean(a.featured));
    });
  }, [
    activeCategory,
    activeGender,
    searchTerm,
    selectedColors,
    selectedSizes,
    sortBy,
    wishlistItems,
    wishlistView,
  ]);

  const activeFilterCount =
    Number(Boolean(activeCategory)) +
    Number(Boolean(activeGender)) +
    selectedSizes.length +
    selectedColors.length;

  const pageTitle = wishlistView
    ? "Wishlist"
    : searchTerm
    ? `Search results for "${searchTerm}"`
    : activeCategory || (activeGender ? `${activeGender}'s collection` : "Shop");

  const categoryHref = (category) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("view");
    if (category) nextParams.set("category", category);
    else nextParams.delete("category");
    return `/shop${nextParams.toString() ? `?${nextParams.toString()}` : ""}`;
  };

  return (
    <main className="catalog" id="main-content">
      <header className="catalog__header">
        <nav className="catalog__breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span>Shop</span>
        </nav>
        <h1>{pageTitle}</h1>
      </header>

      {!wishlistView && !searchTerm && (
        <nav className="catalog__category-nav" aria-label="Product categories">
          <Link
            className={!activeCategory ? "is-active" : ""}
            to={categoryHref("")}
          >
            View all
          </Link>
          {categories.map((category) => (
            <Link
              className={activeCategory === category ? "is-active" : ""}
              key={category}
              to={categoryHref(category)}
            >
              {category}
            </Link>
          ))}
        </nav>
      )}

      <div className="catalog__toolbar">
        <p className="catalog__count" aria-live="polite">
          {filteredProducts.length} {filteredProducts.length === 1 ? "item" : "items"}
        </p>

        <div className="catalog__tools">
          <button
            className="catalog__filter-button"
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
          >
            <FiSliders aria-hidden="true" />
            Filters
            {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
          </button>

          <label className="catalog__sort">
            <span className="sr-only">Sort products</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="price-ascending">Ascending price</option>
              <option value="price-descending">Descending price</option>
              <option value="name">Name</option>
            </select>
            <FiChevronDown aria-hidden="true" />
          </label>
        </div>
      </div>

      {filteredProducts.length ? (
        <section className="catalog__grid" aria-label="Products">
          {filteredProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} eager={index < 4} />
          ))}
        </section>
      ) : (
        <section className="catalog__empty" aria-live="polite">
          <h2>{wishlistView ? "Your wishlist is empty" : "No items found"}</h2>
          <p>
            {wishlistView
              ? "Save pieces you love and find them here whenever you return."
              : "Try removing a filter or using a different search term."}
          </p>
          {wishlistView ? (
            <Link className="catalog__empty-action" to="/shop">
              Discover the collection
            </Link>
          ) : (
            <button className="catalog__empty-action" type="button" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </section>
      )}

      {filtersOpen && (
        <div className="filter-layer">
          <button
            className="filter-layer__backdrop"
            type="button"
            onClick={() => setFiltersOpen(false)}
            aria-label="Close filters"
          />
          <aside
            className="filter-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-title"
          >
            <div className="filter-drawer__header">
              <h2 id="filter-title">Filters</h2>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setFiltersOpen(false)}
                aria-label="Close filters"
                title="Close filters"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="filter-drawer__body">
              <fieldset className="filter-group">
                <legend>Collection</legend>
                {["", "Women", "Men"].map((gender) => (
                  <label key={gender || "all"}>
                    <input
                      type="radio"
                      name="gender"
                      checked={activeGender === gender}
                      onChange={() => updateUrlFilter("gender", gender)}
                    />
                    <span className="filter-group__control">
                      <FiCheck aria-hidden="true" />
                    </span>
                    {gender || "All collections"}
                  </label>
                ))}
              </fieldset>

              <fieldset className="filter-group">
                <legend>Category</legend>
                <label>
                  <input
                    type="radio"
                    name="category"
                    checked={!activeCategory}
                    onChange={() => updateUrlFilter("category", "")}
                  />
                  <span className="filter-group__control">
                    <FiCheck aria-hidden="true" />
                  </span>
                  View all
                </label>
                {categories.map((category) => (
                  <label key={category}>
                    <input
                      type="radio"
                      name="category"
                      checked={activeCategory === category}
                      onChange={() => updateUrlFilter("category", category)}
                    />
                    <span className="filter-group__control">
                      <FiCheck aria-hidden="true" />
                    </span>
                    {category}
                  </label>
                ))}
              </fieldset>

              <fieldset className="filter-group filter-group--grid">
                <legend>Size</legend>
                {sizes.map((size) => (
                  <label key={size}>
                    <input
                      type="checkbox"
                      checked={selectedSizes.includes(size)}
                      onChange={() => toggleArrayFilter(size, setSelectedSizes)}
                    />
                    <span>{size}</span>
                  </label>
                ))}
              </fieldset>

              <fieldset className="filter-group">
                <legend>Colour</legend>
                {colors.map((color) => (
                  <label key={color}>
                    <input
                      type="checkbox"
                      checked={selectedColors.includes(color)}
                      onChange={() => toggleArrayFilter(color, setSelectedColors)}
                    />
                    <span className="filter-group__control">
                      <FiCheck aria-hidden="true" />
                    </span>
                    {color}
                  </label>
                ))}
              </fieldset>
            </div>

            <div className="filter-drawer__footer">
              <button type="button" onClick={clearFilters}>
                Clear filters
              </button>
              <button type="button" onClick={() => setFiltersOpen(false)}>
                Show {filteredProducts.length} items
              </button>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}

export default Shop;
